import React, {useState, useEffect, useMemo, useRef} from 'react';
import update from 'immutability-helper';
import {fetchJson} from '../../../../helpers/fetch.js';
import {Modal, closeModal, Modal as ModalComponent} from '../../../common/Modal.jsx';
import Errors from '../../../common/Errors.jsx';
import Select from '../../../common/Select.jsx';
import FileUploadUppy from '../../../common/uppy_upload/FileUploadUppy.lazy.jsx';
import DropdownMenu from '../../../common/DropdownMenu.jsx';
import styles from '../ProjectPhasesBar.module.scss';
import {PROJECT_VISIBILITY_OPTIONS_MAP} from '../../../result_framework/datagrid/InputConfigurator.jsx';

import {
  project_phase_add_deliverable_path,
  project_phase_available_deliverable_resources_path,
} from '~/routes';

const MODAL_ID = 'add-deliverable-modal';

function AddDeliverableModal({handleCloseModal, project_id, shownPhase, setPhases, deliverableType, dataLoaded, setDataLoaded}) {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const formRef = useRef();

  useEffect(() => {
    if (!dataLoaded) {
      fetchJson(project_phase_available_deliverable_resources_path({project_id})).then(res => {
        setDataLoaded(res);
      })
    }
  }, []);

  const commonIndicatorOptions = useMemo(() => {
    return (dataLoaded?.common_indicator_options||[]).map(v => ({...v, label: I18n.t(PROJECT_VISIBILITY_OPTIONS_MAP[v.label])}));
  }, [dataLoaded]);

  const workflowOptions = useMemo(() => {
    const workflowIds = shownPhase.deliverables.map((v) => v.monitoring_report_id);
    return (dataLoaded?.m_reports||[]).filter(v => !workflowIds.includes(v.id)).map(v => ({value: v.id, label: v.localized_name}));
  }, [dataLoaded]);

  const handleClose = () => {
    closeModal(MODAL_ID);
    handleCloseModal();
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaving(true);

    jQuery.post(project_phase_add_deliverable_path({project_id, phase_id: shownPhase.id}), jQuery(formRef.current).serialize())
      .done((res) => {
        updateDeliverables({...res, completion_percentage: 0, sequence_entries: []});
        setErrors([]);
        handleClose();
      })
      .fail((res) => {
        let responseJSON = res.responseJSON ? res.responseJSON.errors : {server: [{message: I18n.t('unexpected_error')}]};
        setErrors(responseJSON);
        setSaving(false);
      });
  };

  const updateDeliverables = (newDeliverable) => {
    setPhases((oldPhases) => {
      const phaseIdx = oldPhases.findIndex(p => p.id === shownPhase.id);

      return update(oldPhases, {[phaseIdx]: {deliverables: {$push: [newDeliverable]}}});
    });
  }

  const uploadPermissions = {
    'project_deliverable.create': {id: project_id, type: 'project'},
    'project_phase.data': {id: project_id, type: 'project'},
  };

  return (
    <Modal
      id={MODAL_ID}
      title={I18n.t(`add_${deliverableType}`)}
      onSave={handleSave}
      saving={saving}
      saveLabel={I18n.t('save')}
      onClose={handleClose}
      cancelLabel={I18n.t('close')}
      size='md'
      component={ModalComponent}
    >
      <form ref={formRef}>
        <Errors errors={errors}/>

        {deliverableType === 'indicator' ?
          <>
            <div className='form-group'>
              <label>{I18n.t('indicator')} *</label>
              <Select
                name='project_deliverable[indicator_id]'
                className='nested-multiselect'
                options={commonIndicatorOptions}
                placeholder={I18n.t('select_indicator')}
              />
            </div>
          </>
          :
          <div className='form-group'>
            <label>{I18n.t('title')} *</label>
            <input type='text' name='project_deliverable[title]' className='form-control'/>
          </div>
        }

        {deliverableType === 'other_deliverable' &&
          <div className='form-group'>
            <label>{I18n.t('unit')}</label>
            <input type='text' name='project_deliverable[unit]' className='form-control'/>
          </div>
        }

        {['indicator', 'other_deliverable'].includes(deliverableType) &&
          <div className='form-group'>
            <label>{I18n.t('target')}</label>
            <input type='number' name='project_deliverable[target_value]' className='form-control'/>
          </div>
        }

        {deliverableType === 'document_deliverable' &&
          <>
            <div className='form-group'>
              <label>{I18n.t('attachments')}</label>

              <FileUploadUppy
                name={'project_deliverable[attachment_id]'}
                allowedFileTypes={['image/*', '.jpg', '.jpeg', '.png', '.pdf', '.csv', '.doc', '.docx', '.txt', '.xls', '.xlsx', '.xml']}
                maxFileSize={2000000}
                maxNumberOfFiles={1}
                permissions={uploadPermissions}
                isForm={true}
              />
            </div>

            <div className='form-group'>
              <label>{I18n.t('sharepoint_link')}</label>
              <input name='project_deliverable[sharepoint_link]' className='form-control'/>
            </div>
          </>
        }

        {deliverableType === 'recurring_report' &&
          <>
            <div className='form-group'>
              <label>{I18n.t('workflow')} *</label>
              <Select
                name='project_deliverable[monitoring_report_id]'
                className='nested-multiselect'
                options={workflowOptions}
              />
            </div>
          </>
        }
        <input type='hidden' name='project_deliverable[type]' value={deliverableType}/>
      </form>
    </Modal>
  )
}

const AddDeliverable = ({project_id, shownPhase = {}, setPhases}) => {
  const [deliverableType, setDeliverableType] = useState('indicator');
  const [openModal, setOpenModal] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const handleCloseModal = () => setOpenModal(false);
  const handleOpenModal = (type) => {
    setDeliverableType(type)
    setOpenModal(true)
  };

  return (<>
    <DropdownMenu
      menuProps={{style: {margin: 'auto 0px'}}}
      menuItems={
        <>
          {['indicator', 'activity', 'document_deliverable', 'other_deliverable', 'recurring_report'].map((type, i) => {
            return <React.Fragment key={type}>
              <li>
                <a onClick={() => handleOpenModal(type)} href='#'>{I18n.t(type)}</a>
              </li>
              {i < 4 && <li role='separator' className='divider'/>}
            </React.Fragment>
          })}
        </>
      }
    >
      <button className={`flex-btn btn btn-md btn-default ${styles.addButton}`}>
        <i className='fa-solid fa-plus'/>
      </button>
    </DropdownMenu>

    {openModal &&
      <AddDeliverableModal {...{handleCloseModal, project_id, shownPhase, setPhases, deliverableType, dataLoaded, setDataLoaded}}/>
    }
  </>)
}

export default AddDeliverable;
